/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    EMPTY_INVENTORY_STATE
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Implement empty inventory state screen for users with no inventory items.
 * Shows welcoming illustration with three action buttons: Add Item, Upload File,
 * and Download Template. Distinct from filter "no results" empty state.
 *
 * File Size Check:
 * - Current: N/A (new file)
 * - Projected: ~130 lines
 * - Decision: Proceed (well under 200-line limit)
 *
 * Related Screens / Modules:
 * - client/src/features/inventory/InventoryPage.tsx (integration point)
 * - client/public/assets/emptyinventory.svg (new icon asset)
 */

import React from 'react';
import styles from './EmptyInventoryState.module.scss';

interface EmptyInventoryStateProps {
  onAddItem: () => void;
  onUploadFile: () => void;
  onDownloadTemplate: (ext: "csv" | "xlsx") => void;
}

const EmptyInventoryState: React.FC<EmptyInventoryStateProps> = ({
  onAddItem,
  onUploadFile,
  onDownloadTemplate,
}) => {
  // [2025-12-29] Empty state: Local state for template dropdown
  const [showTemplateDropdown, setShowTemplateDropdown] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // [2025-12-29] Empty state: Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowTemplateDropdown(false);
      }
    };

    if (showTemplateDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showTemplateDropdown]);

  return (
    <div className={styles.emptyStateContainer}>
      {/* [2025-12-29] Empty state: Centered illustration */}
      <div className={styles.illustrationWrapper}>
        <img 
          src="/assets/emptyinventory.svg" 
          alt="Empty inventory" 
          className={styles.illustration}
        />
      </div>

      {/* [2025-12-29] Empty state: Title (H3 22, Poppins SemiBold 600) */}
      <h3 className={styles.title}>Your inventory is empty</h3>

      {/* [2025-12-29] Empty state: Description (P1 18, Poppins Regular 400) */}
      <p className={styles.description}>
        Add items or upload an inventory file to start receiving relevant inquiries from buyers
      </p>

      {/* [2025-12-29] Empty state: Action buttons row */}
      <div className={styles.buttonGroup}>
        {/* [2025-12-29] Empty state: Add Item button */}
        <button 
          className={styles.actionButton} 
          onClick={onAddItem}
          type="button"
          aria-label="Add inventory item"
        >
          <span className={styles.plusIcon}>+</span>
          <span>Add Item</span>
        </button>

        {/* [2025-12-29] Empty state: Upload File button */}
        <button 
          className={styles.actionButton} 
          onClick={onUploadFile}
          type="button"
          aria-label="Upload inventory file"
        >
          <svg className={styles.uploadIcon} viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M7 0L7 11M7 0L3 4M7 0L11 4M1 14H13M1 16H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <span>Upload File</span>
        </button>

        {/* [2025-12-29] Empty state: Template button with dropdown */}
        <div className={styles.templateDropdownWrapper} ref={dropdownRef}>
          <button 
            className={styles.actionButton} 
            onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
            type="button"
            aria-label="Download inventory template"
            aria-expanded={showTemplateDropdown}
          >
            <svg className={styles.downloadIcon} viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 0L7 11M7 11L3 7M7 11L11 7M1 14H13M1 16H13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
            <span>Template</span>
          </button>
          
          {/* [2025-12-29] Empty state: Template format dropdown */}
          {showTemplateDropdown && (
            <div className={styles.templateDropdown}>
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  onDownloadTemplate("csv");
                  setShowTemplateDropdown(false);
                }}
                type="button"
              >
                CSV
              </button>
              <button
                className={styles.dropdownItem}
                onClick={() => {
                  onDownloadTemplate("xlsx");
                  setShowTemplateDropdown(false);
                }}
                type="button"
              >
                XLSX
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmptyInventoryState;
